import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Box, Grid, Link, List, ListItem, Typography } from '@mui/material';

import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Terms = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="TaxZerone® Terms of Use: Understanding Our Agreement"
          description="Review TaxZerone's Terms of Use to understand the terms and conditions governing your use of our services. It's important!"
          keywords=""
          canonical="terms-of-use/"
        />
      }
    >
      <Grid
        container
        spacing={2}
        m={2}
        pb={5}
        className="banner-shape-inner"
        sx={{
          mt: { xs: 1, sm: 3 },
          mb: { lg: 5, md: 5, sm: 5 },
          pt: { xs: 0, sm: 0 },
          mx: 'auto',
        }}
        maxWidth="lg"
      >
        <Grid size={12} sx={{}}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { lg: 40, md: 36, sm: 32, xs: 18 },
              fontWeight: 600,
              mb: { xs: 0, sm: 3 },
            }}
          >
            Terms of use - TaxZerone
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            Please read these Terms of Use (“Terms”) carefully before accessing
            or using any website, application, or subdomain operated by
            <span style={{ fontWeight: 600 }}> TaxZerone LLC</span>, including{' '}
            <Link href="/" sx={{ color: '#0202ff' }}>
              TaxZerone.com
            </Link>{' '}
            and any{' '}
            <span style={{ fontWeight: 600 }}>
              white-labeled or partner-branded versions
            </span>{' '}
            (collectively, the “Website” or “Services”).
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            The Website and Services are owned and operated by{' '}
            <span style={{ fontWeight: 600 }}>TaxZerone LLC</span>
            (“TaxZerone,” “we,” “our,” or “us”).
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            By accessing, registering for, or using the Website or Services, you
            (“you,” “your,” or “User”) agree to be legally bound by these Terms.
            If you do not agree, you must not use the Services.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            1. Eligibility and Acceptance
          </Typography>
          <List sx={{ width: { xs: '100%', md: '85%' }, ml: '2%', mb: 0 }}>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              <Box>
                You must be at least{' '}
                <span style={{ fontWeight: 600 }}>18 years of age</span> and
                legally capable of entering into a binding agreement to use the
                Services. By using the Website, you represent and warrant that
                you meet these requirements.
              </Box>
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Your continued use of the Services constitutes acceptance of any
              updates or modifications to these Terms.
            </ListItem>
          </List>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 1.5, sm: 5 },
            }}
          >
            2. Modification of Terms
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            We may revise or update these Terms from time to time. Updated Terms
            will be effective upon posting on the Website or upon notice
            provided by reasonable means. Continued use of the Services after
            such changes constitutes your acceptance of the revised Terms.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 1.5, sm: 5 },
            }}
          >
            3. Description of Services
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            TaxZerone provides an online tax preparation and electronic filing
            platform that allows users to prepare and electronically submit
            federal and applicable state tax forms to the Internal Revenue
            Service (“IRS”) and other tax authorities.
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            TaxZerone is a{' '}
            <span style={{ fontWeight: 600 }}>software provider</span> and{' '}
            <span style={{ fontWeight: 600 }}>electronic transmitter</span>. We
            do not act as your accountant, tax advisor, or legal representative.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 1.5, sm: 5 },
            }}
          >
            4. Mobile Applications
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            TaxZerone may offer mobile applications (“Apps”) for iOS, Android,
            or other platforms. Your use of the Apps is subject to these Terms
            and any additional terms imposed by the applicable app store.
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            We do not guarantee that the Apps will be compatible with all
            devices, operating systems, or versions. We may update, modify, or
            discontinue any App or feature at any time without notice.
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            You acknowledge that Apple Inc., Google LLC, and other app store
            providers are not parties to these Terms and have no responsibility
            for the Apps or their content.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 1.5, sm: 5 },
            }}
          >
            5. No Tax, Legal, or Financial Advice
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            TaxZerone does <span style={{ fontWeight: 600 }}> not</span> provide
            tax, legal, or financial advice.
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            Any information, calculations, prompts, or guidance provided through
            the Website are for{' '}
            <span style={{ fontWeight: 600 }}>
              general informational purposes only
            </span>{' '}
            and are based entirely on the information you supply. You are solely
            responsible for determining the accuracy and appropriateness of your
            tax filings.
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            You are strongly encouraged to consult a qualified tax professional
            regarding your specific tax situation.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 1.5, sm: 5 },
            }}
          >
            6. User Responsibility and Accuracy of Information
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            You are solely responsible for:
          </Typography>
          <List sx={{ width: { xs: '100%', md: '85%' }, ml: '2%', mb: 0 }}>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              <Box>
                {' '}
                The{' '}
                <span style={{ fontWeight: 600 }}>
                  {' '}
                  accuracy, completeness, and truthfulness
                </span>{' '}
                of all data entered into the Website
              </Box>
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Reviewing all tax forms prior to submission
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Ensuring compliance with all applicable federal, state, and local
              tax laws
            </ListItem>
          </List>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            TaxZerone does not independently verify user-provided information
            and is not responsible for errors, penalties, interest, or other
            consequences resulting from inaccurate or incomplete data.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 1.5, sm: 5 },
            }}
          >
            7. Authorization to File and IRS Compliance
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            By submitting a return through TaxZerone, you represent and warrant
            that:
          </Typography>
          <List sx={{ width: { xs: '100%', md: '85%' }, ml: '2%' }}>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              You are the taxpayer, or
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              You are legally authorized to file on behalf of the taxpayer
              (e.g., valid power of attorney or IRS authorization)
            </ListItem>
          </List>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 15 } }}
          >
            Where required by the IRS, you agree to electronically sign and
            retain applicable authorization forms, including but not limited to{' '}
            <span style={{ fontWeight: 600 }}>IRS Forms 8453 and 8879</span> .
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            You acknowledge that IRS acceptance of any return is{' '}
            <span style={{ fontWeight: 600 }}> not guaranteed</span>.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 1.5, sm: 5 },
            }}
          >
            8. Rejected Returns
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            If a return is rejected by the IRS or another tax authority, it is
            your responsibility to:
          </Typography>

          <List sx={{ width: { xs: '100%', md: '85%' }, ml: '2%' }}>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Review the rejection notice
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Correct any identified errors
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Resubmit the return through TaxZerone
            </ListItem>
          </List>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
            id="refund"
          >
            TaxZerone is not responsible for rejected returns or delays caused
            by incorrect or incomplete information.
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            9. Tax Payments and Payment Methods
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            If your tax return reflects a{' '}
            <span style={{ fontWeight: 600 }}>tax balance due</span>, you are
            solely responsible for ensuring that the full amount owed is paid to
            the IRS or applicable tax authority{' '}
            <span style={{ fontWeight: 600 }}>by the required deadline</span>.
          </Typography>
          <Typography
            variant="body1"
            sx={{ my: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            If you choose{' '}
            <span style={{ fontWeight: 600 }}>
              Electronic Funds Withdrawal (EFW)
            </span>{' '}
            at the time of filing, TaxZerone will transmit your payment request
            as authorized.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            If you choose{' '}
            <span style={{ fontWeight: 600 }}>any other payment method</span>,
            including but not limited to:
          </Typography>
          <List sx={{ width: { xs: '100%', md: '85%' }, ml: '2%' }}>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              <span style={{ fontWeight: 600 }}> EFTPS</span>
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              <span style={{ fontWeight: 600 }}> Credit or debit card</span>
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              <span style={{ fontWeight: 600 }}> Check or money order</span>
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              <span style={{ fontWeight: 600 }}> IRS Direct Pay</span>
            </ListItem>
          </List>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            You acknowledge and agree that:
          </Typography>
          <List sx={{ width: { xs: '100%', md: '85%' }, ml: '2%' }}>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              TaxZerone is&nbsp;
              <span style={{ fontWeight: 600 }}> not responsible</span>&nbsp;for
              initiating, processing, confirming, or tracking those payments
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              You are solely responsible for timely payment and compliance
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Any penalties, interest, or enforcement actions resulting from
              non-payment or late payment are your responsibility
            </ListItem>
          </List>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            10. Pricing, Fees, and Payments
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            All prices, fees, discounts, and promotions are subject to change
            without notice. You will be charged the amount displayed at checkout
            at the time your order is completed.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            All fees are non-refundable except as expressly stated in the Refund
            Policy.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            11. Refund Policy
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            Refund requests must be submitted within{' '}
            <span style={{ fontWeight: 600 }}> 30 days</span> of order
            completion.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            Refunds may be issued only under the following circumstances:
          </Typography>
          <List sx={{ width: { xs: '100%', md: '85%' }, ml: '2%' }}>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              You were charged but unable to access your return
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              You were charged multiple times for the same order
            </ListItem>
          </List>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            Refunds <span style={{ fontWeight: 600 }}>will not be issued</span>
            &nbsp;for returns that have already been transmitted to the IRS.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            Approved refunds will be processed within{' '}
            <span style={{ fontWeight: 600 }}>7–10 business days, </span>
            depending on your financial institution.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            12. SMS and Electronic Communications
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            If you opt in to receive SMS/text messages, you expressly consent to
            receive{' '}
            <span style={{ fontWeight: 600 }}>
              {' '}
              transactional and service-related messages
            </span>{' '}
            from TaxZerone at the mobile number you provide.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            Messages may include:
          </Typography>
          <List sx={{ width: { xs: '100%', md: '85%' }, ml: '2%' }}>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Filing status updates
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Submission or acceptance notifications
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Account or security alerts
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Service-related communications
            </ListItem>
          </List>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            Message frequency may vary.{' '}
            <span style={{ fontWeight: 600 }}>
              {' '}
              Message and data rates may apply.
            </span>
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            You may opt out at any time by replying{' '}
            <span style={{ fontWeight: 600 }}>STOP</span>. For assistance, reply
            <span style={{ fontWeight: 600 }}> HELP</span> or contact{' '}
            <Link
              href="mailto:support@taxzerone.com"
              target="_blank"
              sx={{ color: '#0202ff' }}
            >
              support@taxzerone.com
            </Link>
            .
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            Consent to receive SMS messages is{' '}
            <span style={{ fontWeight: 600 }}>
              not a condition of purchase or use
            </span>{' '}
            of the Services.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            13. Electronic Communications and Consent
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            By using the Services, you consent to receive communications
            electronically, including emails, in-app notifications, and SMS
            messages (where opted in).
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            You agree that electronic communications satisfy any legal
            requirement that such communications be in writing. You are
            responsible for maintaining accurate contact information and access
            to electronic communications.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            14. User Conduct
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            You agree not to:
          </Typography>

          <List sx={{ width: { xs: '100%', md: '85%' }, ml: '2%' }}>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Use the Website for unlawful or fraudulent purposes
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Interfere with or disrupt the Website or other users
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Upload malicious software, viruses, or harmful code
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Post false, misleading, defamatory, obscene, or abusive content
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Attempt unauthorized access to systems or data
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Reproduce, distribute, or exploit Website content without
              authorization
            </ListItem>
          </List>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            All Website content is protected under U.S. copyright and
            intellectual property laws.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            15. Monitoring and Compliance
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            We reserve the right to monitor usage of the Website to:
          </Typography>

          <List sx={{ width: { xs: '100%', md: '85%' }, ml: '2%' }}>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Ensure compliance with these Terms
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Protect users and system integrity
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Comply with legal or governmental requests
            </ListItem>
          </List>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            User data and communications may be disclosed if required by law.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            16. Third-Party Services
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            Certain features or services made available through the TaxZerone
            platform may be provided, supported, or facilitated by third parties
            (“Third-Party Services”). These services are offered solely for your
            convenience and may include, without limitation, postal mail
            delivery, payment processing, identity verification, SMS or email
            communications, and data hosting.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            TaxZerone does not control and is not responsible for the
            performance, availability, accuracy, security, pricing, or timelines
            of any Third-Party Services. Delivery times, service availability,
            and outcomes may vary based on the third party’s processes,
            policies, or system availability.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            TaxZerone may add, modify, replace, suspend, or discontinue any
            Third-Party Services at any time, with or without notice, and does
            not guarantee the continued availability of any specific third-party
            feature.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            Your use of any Third-Party Services may be subject to separate
            terms, conditions, and privacy policies imposed by the applicable
            third party. TaxZerone is not responsible for your compliance with
            such third-party terms.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            To the maximum extent permitted by law, TaxZerone shall not be
            liable for any delays, failures, errors, or damages arising from or
            related to the use or unavailability of Third-Party Services.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            17. Disclaimer of Warranties
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            The Website and Services are provided on an{' '}
            <span style={{ fontWeight: 600 }}> “as-is”</span> and
            <span style={{ fontWeight: 600 }}> “as-available”</span> basis.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            TaxZerone disclaims all warranties, express or implied, including
            but not limited to warranties of accuracy, reliability,
            merchantability, fitness for a particular purpose, or uninterrupted
            service.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            18. Limitation of Liability
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            To the maximum extent permitted by law, TaxZerone shall not be
            liable for any indirect, incidental, consequential, special, or
            punitive damages, including penalties, interest, lost profits, or
            lost data.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            TaxZerone’s total liability shall not exceed the amount paid by you
            for the applicable Services.
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            19. Indemnification
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            You agree to indemnify and hold harmless TaxZerone, its affiliates,
            officers, employees, and agents from any claims, damages,
            liabilities, costs, or expenses (including attorneys’ fees) arising
            from:
          </Typography>
          <List sx={{ width: { xs: '100%', md: '85%' }, ml: '2%' }}>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Your use of the Services
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Your violation of these Terms
            </ListItem>
            <ListItem
              sx={{
                alignItems: 'flex-start',
                fontSize: { xs: 13, sm: 16, md: 16 },
              }}
            >
              <ArrowForwardRoundedIcon sx={{ mr: 1 }} />
              Your submission of inaccurate or unauthorized tax information
            </ListItem>
          </List>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            20. Termination
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            We may suspend or terminate your account and access to the Services
            at any time if we believe you have violated these Terms or
            applicable laws.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            21. Privacy
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            Your use of the Services is subject to our{' '}
            <span style={{ fontWeight: 600 }}>Privacy Policy,</span> which
            describes how we collect, use, and protect your information.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            22. Governing Law
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            These Terms shall be governed by and construed in accordance with
            the laws of the{' '}
            <span style={{ fontWeight: 600 }}> State of California,</span>{' '}
            United States, without regard to conflict-of-law principles.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 32, md: 28, sm: 24, xs: 16 },
              fontWeight: 600,
              mb: 0,
              mt: { xs: 2.5, sm: 5 },
            }}
          >
            23. Contact Information
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: { xs: 13, sm: 16, md: 16 } }}
          >
            If you have questions regarding these Terms, please contact us at:{' '}
            <Link
              href="mailto:support@taxzerone.com"
              target="_blank"
              sx={{ color: '#0202ff' }}
            >
              support@taxzerone.com
            </Link>
          </Typography>
        </Grid>
      </Grid>
    </AppLayout>
  );
};

export default Terms;
